import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { usePeut } from '../../auth/usePeut.js';
import { listerOuvertures } from '../../lib/api.js';
import { estClos } from '../../lib/ouvertures.js';
import pageStyles from '../../styles/page.module.css';
import styles from './Ouvertures.module.css';

/**
 * Le bandeau « Mise en service en cours » de la fiche galerie (ARTO-128) : il
 * renvoie au dossier d'ouverture tant que la galerie n'est pas en service. Il
 * ne se montre qu'à qui peut lire les dossiers, et se tait si la liste est
 * illisible (la fiche galerie n'en dépend pas).
 *
 * Limite : l'API ne filtre pas les dossiers par galerie ; on lit la première
 * page, triée par dernière modification, où se trouvent les dossiers actifs.
 */
export function BandeauMiseEnService({ idGalerie }: { idGalerie: string }): JSX.Element | null {
  const { t } = useTranslation('console');
  const peut = usePeut();
  const autorise = peut('console:read-onboarding');
  const requete = useQuery({
    queryKey: ['console', 'ouvertures', 'bandeau'],
    queryFn: () => listerOuvertures({}),
    enabled: autorise,
  });
  const dossier = requete.data?.items.find(
    (ligne) => ligne.galleryId === idGalerie && !estClos(ligne.etat),
  );
  if (!autorise || !dossier) return null;
  return (
    <div className={pageStyles.banniere} role="status">
      <span>
        <strong>{t('ouvertures.bandeau.titre')}</strong>{' '}
        {t('ouvertures.bandeau.texte', {
          numero: dossier.numero,
          etat: t(`ouvertures.etat.${dossier.etat}`),
        })}
      </span>
      <Link to={`/ouvertures/${dossier.id}`} className={styles.bandeauLien}>
        {t('ouvertures.bandeau.lien')}
      </Link>
    </div>
  );
}
